// Cross-batch registry of field-added asset_type id rewrites (H19 #703).
//
// When a field-added asset_type PUT collides on its (sub_system, name) scope
// (already_exists), connector-dispatch-asset-types.ts re-homes the variant onto
// the server's authoritative row at a DIFFERENT id and deletes the local
// orphan. Any capture_items the surveyor recorded against the orphan asset type
// carry the orphan asset_type_id in their queued op data; this registry lets the
// createItem dispatcher (connector-dispatch-create-item.ts) redirect those POSTs
// onto the authoritative id instead of POSTing a now-deleted id → 404 → the
// count gets stranded in quarantine (the create-item 404 branch only attempts
// SESSION recovery, not asset-type recovery).
//
// MODULE-LEVEL, not the per-batch DispatchContext, so the mapping survives
// ACROSS CRUD batches within an app session. PowerSync's getCrudBatch caps a
// batch at 100 entries, so a large offline field session (adding a variant then
// capturing >100 mutations) can upload the colliding asset_type create in one
// batch and a dependent count in a LATER batch — the per-batch
// context.assetTypeIdRewrites map is discarded between them and the count would
// POST the stale id. uploadData runs batches sequentially in the same JS
// context, so module state bridges them. Exactly mirrors the module-level
// connector-rehome-registry.ts (capture_item increment re-homes) and the
// lwwAttempts map in connector-dispatch-item-patch.ts.
//
// Keys are client-generated UUIDs (globally unique), so a stale entry can never
// mis-match a different row; entries are tiny and bounded by the number of
// distinct field-add collisions in a session.
//
// Residual: the mapping is in-memory, so a full app RESTART in the narrow window
// between the asset_type create's batch acking and a dependent count's later
// batch falls back to the POST-stale-id path (which 404s and quarantines the
// count — preserved, not lost). Durable (local SQLite) persistence would close
// that, but is disproportionate to the restart-mid-upload window and would add
// client-schema surface — deferred, identical to the capture_item re-home
// registry's documented residual.

const assetTypeRewrites = new Map<string, string>()

/** Record orphan asset_type id → authoritative id (set by the field-add 409
 *  carve-out in connector-dispatch-asset-types.ts). */
export function recordAssetTypeRewrite(
  orphanId: string,
  authoritativeId: string,
): void {
  assetTypeRewrites.set(orphanId, authoritativeId)
}

/** Look up the authoritative asset_type id for an orphan id, if it was
 *  re-homed in this or an earlier batch; undefined when there is no rewrite. */
export function getAssetTypeRewrite(orphanId: string): string | undefined {
  return assetTypeRewrites.get(orphanId)
}

/** Test-only reset so cases don't leak rewrites across `it` blocks. */
export function _resetAssetTypeRewrites(): void {
  assetTypeRewrites.clear()
}
